import { fetchWithAuth } from "../api/client";
import { ErrorResponse, EmptyResponse, Question } from "../types";
export type GetQuestionsFilters = {
  isGeneric?: boolean;
  surveyId?: string;
  organizationId?: string;
  includeAudioFiles?: boolean;
};

function buildQueryString(filters?: GetQuestionsFilters): string {
  if (!filters) return "";
  const p = new URLSearchParams();
  if (typeof filters.isGeneric === "boolean")
    p.set("isGeneric", String(filters.isGeneric));
  if (filters.surveyId) p.set("surveyId", filters.surveyId);
  if (filters.organizationId) p.set("organizationId", filters.organizationId);
  if (typeof filters.includeAudioFiles === "boolean")
    p.set("includeAudioFiles", String(filters.includeAudioFiles));
  const qs = p.toString();
  return qs ? `?${qs}` : "";
}
export async function getQuestions(
  token: string,
  filters?: GetQuestionsFilters
): Promise<Question[]> {
  const qs = buildQueryString({ ...(filters ?? {}), includeAudioFiles: filters?.includeAudioFiles ?? true });
  const res = await fetchWithAuth(`Questions${qs}`, { method: "GET", token });
  if (!res.ok) throw new Error(`Error fetching questions: ${res.statusText}`);
  const data = await res.json();

  return data.map((q: any): Question => {
    const audioFiles: Record<"es" | "ca" | "en" | "ar", File | null> = {
      es: null,
      ca: null,
      en: null,
      ar: null,
    };
    const audioUrls: Record<"es" | "ca" | "en" | "ar", string> = {
      es: "",
      ca: "",
      en: "",
      ar: "",
    };

    if (q.audioFiles) {
      for (const lang of Object.keys(q.audioFiles)) {
        const f = q.audioFiles[lang];
        const bytes = Uint8Array.from(atob(f.base64), (c) => c.charCodeAt(0));
        const blob = new Blob([bytes], { type: f.contentType || "audio/mpeg" });
        const file = new File([blob], f.fileName || `voice-${lang}.mp3`, {
          type: blob.type,
        });
        audioFiles[lang as "es" | "ca" | "en" | "ar"] = file;
        audioUrls[lang as "es" | "ca" | "en" | "ar"] =
          URL.createObjectURL(blob);
      }
    }

    // Map audioUrls from API if they exist (as strings like "voice/u0zeg10w.mp3")
    const apiAudioUrls = q.audioUrls || {};
    const finalAudioUrls: Record<"es" | "ca" | "en" | "ar", string> = {
      es: audioUrls.es || apiAudioUrls.es || "",
      ca: audioUrls.ca || apiAudioUrls.ca || "",
      en: audioUrls.en || apiAudioUrls.en || "",
      ar: audioUrls.ar || apiAudioUrls.ar || "",
    };

    return {
      id: q.id,
      text: {
        es: q.textEs,
        ca: q.textCa,
        en: q.textEn ?? "",
        ar: q.textAr ?? "",
      },
      audioFiles,
      audioUrls: finalAudioUrls,
      audioObsKeys: q.audioObsKeys || q.audioUrls || {},
      type: q.type,
      isGeneric: typeof q.isGeneric === "boolean" ? q.isGeneric : q.type === 0,
      order: q.order ?? 0,
      createdAt: q.createdAt || undefined,
      organizationId: q.organizationId ?? undefined,
      organizationName: q.organizationName ?? q.organization?.name,
    };
  });
}
export async function getQuestionsBySurvey(
  surveyId: string,
  token: string
): Promise<Question[]> {
  const res = await fetchWithAuth(
    `Questions?surveyId=${encodeURIComponent(surveyId)}`,
    { method: "GET", token }
  );
  if (!res.ok) throw new Error("Failed to fetch survey questions");
  const data = await res.json();
  return data.map(
    (q: any): Question => ({
      id: q.id,
      text: {
        es: q.textEs,
        ca: q.textCa,
        en: q.textEn ?? "",
        ar: q.textAr ?? "",
      },
      audioUrls: q.audioUrls || { es: "", ca: "", en: "", ar: "" },
      audioUrl: q.audioUrl || undefined,
      type: q.type,
      isGeneric: typeof q.isGeneric === "boolean" ? q.isGeneric : q.type === 0,
      order: q.order ?? 0,
      organizationId: q.organizationId ?? undefined,
    })
  );
}

export async function getQuestionById(
  id: string,
  token: string,
  includeAudioFiles: boolean = false
): Promise<Question | ErrorResponse> {
  const queryParam = includeAudioFiles ? "?includeAudioFiles=true" : "";
  const res = await fetchWithAuth(`Questions/${id}${queryParam}`, { method: "GET", token });
  if (!res.ok)
    return {
      error: res.status,
      message: `Error fetching question: ${res.statusText}`,
    };
  const q = await res.json();
  
  // Process audio files if included
  const audioFiles: Record<"es" | "ca" | "en" | "ar", File | null> = {
    es: null,
    ca: null,
    en: null,
    ar: null,
  };
  const audioUrls: Record<"es" | "ca" | "en" | "ar", string> = {
    es: "",
    ca: "",
    en: "",
    ar: "",
  };

  if (includeAudioFiles && q.audioFiles) {
    for (const lang of Object.keys(q.audioFiles)) {
      const f = q.audioFiles[lang];
      const bytes = Uint8Array.from(atob(f.base64), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: f.contentType || "audio/mpeg" });
      const file = new File([blob], f.fileName || `voice-${lang}.mp3`, {
        type: blob.type,
      });
      audioFiles[lang as "es" | "ca" | "en" | "ar"] = file;
      audioUrls[lang as "es" | "ca" | "en" | "ar"] =
        URL.createObjectURL(blob);
    }
  }

  // Map audioUrls from API if they exist (as strings like "voice/u0zeg10w.mp3")
  const apiAudioUrls = q.audioUrls || {};
  const finalAudioUrls: Record<"es" | "ca" | "en" | "ar", string> = {
    es: audioUrls.es || apiAudioUrls.es || "",
    ca: audioUrls.ca || apiAudioUrls.ca || "",
    en: audioUrls.en || apiAudioUrls.en || "",
    ar: audioUrls.ar || apiAudioUrls.ar || "",
  };

  return {
    id: q.id,
    text: {
      es: q.textEs,
      ca: q.textCa,
      en: q.textEn ?? "",
      ar: q.textAr ?? "",
    },
    audioFiles: includeAudioFiles ? audioFiles : undefined,
    audioUrls: finalAudioUrls,
    audioObsKeys: q.audioObsKeys || q.audioUrls || {},
    audioUrl: q.audioUrl || undefined,
    type: q.type,
    surveyId: q.surveyId,
    order: q.order ?? 0,
    isGeneric: typeof q.isGeneric === "boolean" ? q.isGeneric : q.type === 0,
    organizationId: q.organizationId ?? undefined,
    organizationName: q.organizationName ?? q.organization?.name,
    createdAt: q.createdAt || undefined,
  };
}

export async function createQuestion(
  data: Omit<Question, "id" | "createdAt">,
  token: string
): Promise<Question | ErrorResponse | EmptyResponse> {
  const payload = {
    textEs: data.text.es,
    textCa: data.text.ca,
    textEn: data.text.en ?? null,
    textAr: data.text.ar ?? null,
    audioUrl: data.audioUrl,
    type: data.type,
    audioUrls: data.audioObsKeys ||
      data.audioUrls || { es: "", ca: "", en: "", ar: "" },
    organizationId: data.organizationId ?? null,
  };

  try {
    const res = await fetchWithAuth("Questions", {
      method: "POST",
      token,
      body: JSON.stringify(payload),
    });
    if (!res.ok)
      return {
        error: res.status,
        message: `Error creating question: ${res.statusText}`,
      };
    return { ok: true };
  } catch (err: any) {
    return { error: 500, message: err.message };
  }
}

export async function updateQuestion(
  id: string,
  data: Omit<Question, "createdAt">,
  token: string
): Promise<EmptyResponse | ErrorResponse> {
  const payload = {
    id,
    textEs: data.text.es,
    textCa: data.text.ca,
    textEn: data.text.en ?? null,
    textAr: data.text.ar ?? null,
    audioUrl: data.audioUrl,
    type: data.type,
    audioUrls: data.audioObsKeys ||
      data.audioUrls || { es: "", ca: "", en: "", ar: "" },
    organizationId: data.organizationId ?? null,
  };

  const res = await fetchWithAuth(`Questions/${id}`, {
    method: "PUT",
    token,
    body: JSON.stringify(payload),
  });
  if (!res.ok)
    return {
      error: res.status,
      message: `Error updating question: ${res.statusText}`,
    };
  return { ok: true };
}

export async function deleteQuestion(id: string, token: string) {
  const res = await fetchWithAuth(`Questions/${id}`, {
    method: "DELETE",
    token,
  });

  if (!res.ok) {
    return {
      error: res.status,
      message: `Error deleting question: ${res.statusText}`,
    };
  }

  return { ok: true };
}
